import { React, Component, useEffect, useState, useMemo } from 'react';
import {
    APIProvider, Map, AdvancedMarker, useMarkerRef, Pin,
    InfoWindow, useMap, MapControl, ControlPosition
} from '@vis.gl/react-google-maps';

/** Child component that fits bounds to the given items (lat/lng) */
const FitBoundsOnFilter = ({ items, padding = { top: 64, right: 16, bottom: 16, left: 16 } }) => {
    const map = useMap();
    useEffect(() => {
        if (!map || !items?.length) return;

        let north = -90, south = 90, east = -180, west = 180;
        for (const { lat, lng } of items) {
            north = Math.max(north, lat);
            south = Math.min(south, lat);
            east = Math.max(east, lng);
            west = Math.min(west, lng);
        }
        map.fitBounds({ north, south, east, west }, padding);
    }, [map, items, padding]);

    return null;
};

const TripMap = (props) => {
    const [markerRef, marker] = useMarkerRef();
    const [userLocation, setUserLocation] = useState(null);

    // Segment filter state (null = All)
    const [selectedSegmentOrder, setSelectedSegmentOrder] = useState(null);

    // Detect user location
    useEffect(() => {
        if (navigator.geolocation) {
            navigator.geolocation.getCurrentPosition(
                (position) =>
                    setUserLocation({ lat: position.coords.latitude, lng: position.coords.longitude }),
                (error) => console.error("Geolocation error:", error)
            );
        }
    }, []);

    // Data helpers
    const segments = Array.isArray(props?.data) ? props.data : [];

    const filteredSegments = useMemo(() => {
        if (selectedSegmentOrder == null) return segments;
        return segments.filter(s => Number(s?.order) === selectedSegmentOrder);
    }, [segments, selectedSegmentOrder]);

    // Flatten filtered activities (for markers + fitBounds)
    const filteredActivities = useMemo(() => {
        const out = [];
        for (const s of filteredSegments) {
            for (const a of (s.activities || [])) {
                if (!a?.coordinates_lat || !a?.coordinates_lng) continue;
                out.push({
                    segOrder: s.order,
                    actOrder: a.order,
                    key: s.order * 1000 + a.order,
                    lat: parseFloat(a.coordinates_lat),
                    lng: parseFloat(a.coordinates_lng),
                    label: a.label
                });
            }
        }
        return out;
    }, [filteredSegments]);

    // Initial center/zoom logic (unchanged)
    let coordinates_lat = 0.0, coordinates_lng = 0.0, zoom = 3;
    try {
        if (userLocation?.lat) {
            coordinates_lat = parseFloat(userLocation.lat);
            coordinates_lng = parseFloat(userLocation.lng);
            zoom = 10;
        } else if (props.activity_selected && segments.length >= 1) {
            for (const segment of segments) {
                for (const activity of segment.activities || []) {
                    if (props.activity_selected === (segment.order * 1000 + activity.order)) {
                        coordinates_lat = parseFloat(activity.coordinates_lat);
                        coordinates_lng = parseFloat(activity.coordinates_lng);
                        zoom = 10;
                    }
                }
            }
        } else if (segments.length >= 1 && segments[0]?.activities?.[0]) {
            coordinates_lat = parseFloat(segments[0].activities[0].coordinates_lat);
            coordinates_lng = parseFloat(segments[0].activities[0].coordinates_lng);
            zoom = 10;
        }
    } catch (err) { console.log("No activities to display"); }

    const selectedActivity = useMemo(
        () => filteredActivities.find(a => a.key === props.activity_selected),
        [filteredActivities, props.activity_selected]
    );

    const segLabel = (s) => s?.title || s?.label || `Segment ${s?.order ?? ''}`;

    return (
        <APIProvider apiKey={"AIzaSyCT6qVODgp54aNqtALAB3w324rqBtEr_5s"}>
            <Map
                className={"trip__map-inner"}
                defaultZoom={zoom}
                zoom={zoom > 10 ? zoom : null}
                defaultCenter={{ lat: coordinates_lat, lng: coordinates_lng }}
                gestureHandling={"greedy"}
                mapId={"trip_map"}
            >
                {/* SEGMENT FILTER OVERLAY */}
                {props.data.length > 1 &&
                    <MapControl position={ControlPosition.TOP_RIGHT} className="trip__map__filter-container">
                        <div className="trip__map__filter">
                            <select
                                value={selectedSegmentOrder ?? ''}
                                onChange={(e) => {
                                    const val = e.target.value;
                                    setSelectedSegmentOrder(val === '' ? null : Number(val)); // <-- ensure number
                                }}
                                className="trip__map__filter__select"
                            >
                                <option value="">All segments</option>
                                {segments.map((s) => (
                                    <option key={s.order} value={s.order}>{segLabel(s)}</option>
                                ))}
                            </select>
                            <button
                                onClick={() => setSelectedSegmentOrder(null)}
                                className="trip__map__filter__button"
                                title="Reset filters"
                            >
                                Reset
                            </button>
                            <button
                                className="trip__map__close__button"
                                aria-label="Close map"
                                onClick={(e) => { e.stopPropagation(); props.toggleMap?.(); }}
                            >
                                Close
                            </button>
                        </div>
                    </MapControl>
                }

                {/* Filtered markers only */}
                {filteredActivities.map((a) => (
                    <AdvancedMarker
                        key={a.key}
                        className={"trip__map__marker"}
                        title={a.label || "Trip activity"}
                        ref={props.activity_selected === a.key ? markerRef : null}
                        position={{ lat: a.lat, lng: a.lng }}
                        onClick={() => props.selectMapMarker(a.key)}
                    >
                        {props.activity_selected === a.key
                            ? <Pin background={"#aa5375"} glyphColor={"#fbfcfb"} borderColor={"#fbfcfb"} />
                            : <Pin background={"#a98f99"} glyphColor={"#fbfcfb"} borderColor={"#fbfcfb"} />
                        }
                    </AdvancedMarker>
                ))}

                {/* InfoWindow (shows only if selected is within current filter) */}
                {selectedActivity && (
                    <InfoWindow anchor={marker}>
                        <p className="trip__map__infowindow text-bold text-color-secondary">
                            {selectedActivity.label}
                        </p>
                    </InfoWindow>
                )}

                {/* Auto-fit to visible pins */}
                <FitBoundsOnFilter items={filteredActivities} />

                {/* User location */}
                {userLocation && <UserLocationCircle center={userLocation} />}
            </Map>
        </APIProvider>
    );
};

export default TripMap;

// Your existing helper stays the same
const UserLocationCircle = ({ center }) => {
    return (
        <AdvancedMarker position={center}>
            <div style={{ position: 'relative', width: '48px', height: '48px' }}>
                <span style={{
                    position: 'absolute', top: '50%', left: '50%', width: '48px', height: '48px',
                    marginLeft: '-24px', marginTop: '-24px', borderRadius: '50%',
                    backgroundColor: 'rgba(66, 133, 244, 0.3)', animation: 'pulse-ring 1.5s infinite ease-out'
                }} />
                <span style={{
                    position: 'absolute', top: '50%', left: '50%', width: '20px', height: '20px',
                    marginLeft: '-10px', marginTop: '-10px', backgroundColor: '#4285F4',
                    border: '4px solid white', borderRadius: '50%', zIndex: 2, boxShadow: '0 0 6px #4285F4'
                }} />
            </div>
        </AdvancedMarker>
    );
};
